import { SupportedLanguage } from '@store/i18n';
import type { TFunction } from 'i18next';
import {
  CsvMappedValue,
  CsvValidationError,
  CsvValidationResultData,
  ValidationRule,
} from '../../types/csvValidation.types';
import { ImportReportDataKeys } from '@screens/ReportCreation/components/CsvImport/types/csvParser.types';
import { buildValidationRules } from '@screens/ReportCreation/components/CsvImport/utils/validation/csvValidationRules.utils';
import {
  normalizeValue,
  objectIsEmpty,
} from '@screens/ReportCreation/components/CsvImport/utils/csvParser.utils';
import { CsvValueOut } from '@screens/ReportCreation/components/CsvImport/types/csvImport.types';
import {
  DIAGNOSTIC_PROCEDURES_TEMPLATE_LIMIT,
  getDiagnosticBasePath,
} from '@screens/ReportCreation/components/CsvImport/constants/csvTemplate.constants';
import type { ReferentialsResponseDto } from '@api/WhisApi/generated';
import { FormOption } from '@screens/ReportCreation/reportCreation.types';

export const parseNumberValue = (value: string): number | null => {
  const normalized = normalizeValue(value).replace(',', '.');
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return parsed;
};

export const isValidDateYYYYMMDD = (value: string): boolean => {
  const normalized = normalizeValue(value);
  if (!normalized) {
    return false;
  }

  const isoLike = normalized.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
  if (isoLike) {
    const [, yearValue, monthValue, dayValue] = isoLike;
    const year = Number(yearValue);
    const month = Number(monthValue);
    const day = Number(dayValue);
    const date = new Date(Date.UTC(year, month - 1, day));

    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() + 1 === month &&
      date.getUTCDate() === day
    );
  }

  const dayFirst = normalized.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (!dayFirst) {
    return false;
  }

  const [, dayValue, monthValue, yearValue] = dayFirst;
  const day = Number(dayValue);
  const month = Number(monthValue);
  const year = Number(yearValue);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() + 1 === month &&
    date.getUTCDate() === day
  );
};

/**
 * Return a map with, for each diagnostic item index, whether it has been filled or not
 * @return diagnosticItemValues
 */
const buildDiagnosticItemHasAnyValue = (
  report: Record<ImportReportDataKeys, string>,
): Record<number, boolean> => {
  const diagnosticHasAnyValue: Record<number, boolean> = {};

  for (
    let index = 0;
    index < DIAGNOSTIC_PROCEDURES_TEMPLATE_LIMIT;
    index += 1
  ) {
    const diagnosticBasePath = getDiagnosticBasePath(index);
    diagnosticHasAnyValue[index] = Object.entries(report).some(
      ([key, value]: [string, string]) =>
        key.startsWith(diagnosticBasePath) && value,
    );
  }

  return diagnosticHasAnyValue;
};

const evaluateRequired = (
  rule: ValidationRule,
  causeKnown: boolean,
  diagnosticHasAnyValue: Record<number, boolean>,
  diagnosticEnabled: boolean,
): boolean => {
  let isRequired = rule.required;

  if (
    (rule.fieldId === 'etiologyType' || rule.fieldId === 'etiologyAgent') &&
    isRequired
  ) {
    // Etiology type && Etiology agent can only be required if cause known
    isRequired = causeKnown;
  }
  if (rule.diagnosticIndex !== undefined && isRequired) {
    isRequired =
      diagnosticHasAnyValue[rule.diagnosticIndex] && diagnosticEnabled;
  }

  return isRequired;
};

/**
 * @return Error label key
 */
const validateNumericRule = (
  rule: ValidationRule,
  value: string,
  report: Record<ImportReportDataKeys, string>,
): string | undefined => {
  const numericValue = parseNumberValue(value);
  if (numericValue === null) {
    return 'reportCreation.csvImportValidation.errors.invalidNumber';
  }

  if (rule.fieldType === 'latitude') {
    if (numericValue < -90 || numericValue > 90) {
      return 'reportCreation.csvImportValidation.errors.invalidLatitude';
    }
    return;
  }

  if (rule.fieldType === 'longitude') {
    if (numericValue < -180 || numericValue > 180) {
      return 'reportCreation.csvImportValidation.errors.invalidLongitude';
    }
    return;
  }

  if (numericValue < 0) {
    return 'reportCreation.csvImportValidation.errors.numberBelowZero';
  }

  if (rule.fieldId === 'populationSize' && numericValue <= 0) {
    return 'reportCreation.csvImportValidation.errors.populationMustBeAboveZero';
  }

  if (rule.diagnosticIndex && rule.fieldId.endsWith('results.totalCount')) {
    const diagnosticStartKey = `diagnosticProcedures.${rule.diagnosticIndex}.results.`;
    const positiveValue =
      parseNumberValue(report[diagnosticStartKey + `positiveCount`]) ?? 0;
    const negativeValue =
      parseNumberValue(report[diagnosticStartKey + `negativeCount`]) ?? 0;

    if (numericValue < positiveValue + negativeValue) {
      return 'reportCreation.csvImportValidation.errors.totalCountTooLow';
    }
  }
};

const getDynamicOptions = (
  rule: ValidationRule,
  linkedValue: string,
  linkedRule: ValidationRule,
): FormOption[] => {
  if (!rule.dynamicOptions) return [];
  const correspondingValue = normalizeValue(linkedValue);
  const dependFieldValue = mapRawValueFromRule(linkedRule, correspondingValue);
  return rule.dynamicOptions.optionsByValue[String(dependFieldValue)] || [];
};

const validateRuleValue = (
  rule: ValidationRule,
  report: Record<ImportReportDataKeys, string>,
  t: TFunction,
  linkedRule?: ValidationRule,
): string | undefined => {
  const value = normalizeValue(report[rule.fieldId]);

  switch (rule.fieldType) {
    case 'option': {
      const options = rule.options?.map((option) => option);
      if (options?.length && !options.includes(value.toLowerCase())) {
        return t('reportCreation.csvImportValidation.errors.invalidOption');
      }
      break;
    }
    case 'dynamic': {
      if (!linkedRule) return;
      const linkedValue = report[linkedRule.fieldId];
      const validValues = getDynamicOptions(rule, linkedValue, linkedRule);
      const validLabels = new Set(
        validValues.map((opt) => opt.label.toLowerCase()),
      );

      if (validLabels.size > 0 && !validLabels.has(value.toLowerCase())) {
        return t(
          'reportCreation.csvImportValidation.errors.invalidDynamicOption',
        );
      }

      break;
    }
    case 'date':
      if (!isValidDateYYYYMMDD(value)) {
        return t('reportCreation.csvImportValidation.errors.invalidDate');
      }
      break;
    case 'numeric':
    case 'latitude':
    case 'longitude': {
      const errorKey = validateNumericRule(rule, value, report);
      return errorKey ? t(errorKey) : undefined;
    }

    case 'text':
      if (typeof rule.maxLength === 'number' && value.length > rule.maxLength) {
        return t('reportCreation.csvImportValidation.errors.textTooLong', {
          count: rule.maxLength,
        });
      }
      break;
  }
};

const mapRawValueFromRule = (
  rule: ValidationRule,
  rawValue: string,
  linkedRule?: ValidationRule,
  linkedValue?: string,
): CsvMappedValue => {
  if (rawValue === '') {
    return undefined;
  }

  if (rule.fieldType === 'option' && rule.optionValueByLabel) {
    const mappedValue =
      rule.optionValueByLabel[rawValue.toLowerCase()] ?? rawValue;

    if (rule.fieldId === 'inaccurateLocation') {
      if (mappedValue.toLowerCase() === 'yes') {
        return true;
      }
      if (mappedValue.toLowerCase() === 'no') {
        return false;
      }
      return undefined;
    }

    return mappedValue;
  }
  if (rule.fieldType === 'dynamic') {
    if (!linkedRule || !linkedValue) return rawValue;
    const dynamicOptions = getDynamicOptions(rule, linkedValue, linkedRule);
    const formValue = dynamicOptions.find(
      (opt) => opt.label.toLowerCase() === rawValue.toLowerCase(),
    );

    return formValue?.value || rawValue;
  }

  if (
    rule.fieldType === 'numeric' ||
    rule.fieldType === 'latitude' ||
    rule.fieldType === 'longitude'
  ) {
    return parseNumberValue(rawValue) ?? undefined;
  }

  return rawValue;
};

export const validateARule = (
  rule: ValidationRule,
  diagnosticValues: Record<number, boolean>,
  diagnosticEnabled: boolean,
  report: Record<ImportReportDataKeys, string>,
  t: TFunction,
  linkedRule?: ValidationRule,
): string | undefined => {
  const yesLabel = t('reportCreation.step2.options.yesNo.yes');
  const value = report[rule.fieldId];
  const isRequired = evaluateRequired(
    rule,
    report.knowTheCause === yesLabel,
    diagnosticValues,
    diagnosticEnabled,
  );

  if (!value && isRequired) {
    return t('reportCreation.csvImportValidation.errors.requiredField');
  }
  if (!value) {
    return undefined; // That means : no error
  }
  return validateRuleValue(rule, report, t, linkedRule);
};

export const validateReports = (
  reports: Record<ImportReportDataKeys, string>[],
  t: TFunction,
  language: SupportedLanguage,
  referentials?: ReferentialsResponseDto,
): CsvValidationResultData<Record<ImportReportDataKeys, CsvValueOut>> => {
  const validationRules = buildValidationRules(t, language, referentials);
  const errors: CsvValidationError[] = [];
  let invalidRows = 0;
  const validReports = reports
    .filter((report) => !objectIsEmpty(report)) // Filter empty lines
    .map((report, index) => {
      const validatingReport: Record<ImportReportDataKeys, CsvValueOut> = {
        ...report,
      };
      const diagnosticValues = buildDiagnosticItemHasAnyValue(report);
      const diagnosticEnabled =
        report['diagnosticProcedure'].toLowerCase() === 'yes';
      let valid = true;

      validationRules.forEach((rule) => {
        let linkedRule: ValidationRule | undefined;
        let linkedValue = '';
        if (rule.dynamicOptions?.dependsOn) {
          linkedRule = validationRules.find(
            (lRule) => lRule.fieldId === rule.dynamicOptions!.dependsOn,
          );
          linkedValue = linkedRule ? report[linkedRule.fieldId] : '';
        }
        const error = validateARule(
          rule,
          diagnosticValues,
          diagnosticEnabled,
          report,
          t,
          linkedRule,
        );

        if (error) {
          errors.push({
            rowNumber: index + 2, // Start with 1 and avoid header
            columnHeader: rule.header,
            message: error,
          });
          valid = false;
        }
        validatingReport[rule.fieldId] = mapRawValueFromRule(
          rule,
          report[rule.fieldId],
          linkedRule,
          linkedValue,
        );
      });
      if (!valid) invalidRows++;
      return validatingReport;
    });
  return {
    totalRows: validReports.length,
    errors,
    parsedRows: validReports as Record<ImportReportDataKeys, CsvValueOut>[],
    invalidRows,
  };
};
